import React, { useState, useEffect } from 'react';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import ProductService from '../../services/ProductService';
import ProductCard from '../ProductCard';
import FeaturedProductsPanel from './FeaturedProductsPanel';
import { getFeaturedProducts, MAX_FEATURED_PRODUCTS } from '../../utils/featuredProducts';
import { getFeaturedToggleState, isActiveProduct } from '../../utils/productStatus';
import './ProductList.css';
import './FeaturedProductsPanel.css';
import { useNavigate } from 'react-router-dom';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const navigate = useNavigate();

  // Charger les produits au chargement du composant
  useEffect(() => {
    loadProducts();
  }, []);

  // Fonction pour charger les produits depuis l'API
  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await ProductService.getProducts();
      // Le ProductListHandler retourne { success: true, data: [...], total: number }
      if (response.success && response.data) {
        setProducts(response.data);
      } else {
        console.error('Format de réponse inattendu:', response);
        setProducts([]);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour supprimer un produit
  const handleDeleteProduct = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await ProductService.deleteProduct(id);
        // Recharger la liste après suppression
        loadProducts();
      } catch (error) {
        console.error('Failed to delete product:', error);
      }
    }
  };

  // Filtrer les produits en fonction de la recherche et du filtre de catégorie
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.reference.toLowerCase().includes(searchTerm.toLowerCase());

    // Nouvelle logique de filtrage par catégorie basée sur le tableau categories
    const matchesCategory = filterCategory
      ? product.categories && product.categories.some((cat) => cat.id === parseInt(filterCategory, 10))
      : true;

    return matchesSearch && matchesCategory;
  });

  // Extraire les catégories uniques pour le filtre
  const categories = [];
  const categoryMap = new Map();

  products.forEach((product) => {
    if (product.categories && Array.isArray(product.categories)) {
      product.categories.forEach((category) => {
        if (!categoryMap.has(category.id)) {
          categoryMap.set(category.id, category);
          categories.push(category);
        }
      });
    }
  });

  const handleEditProduct = (productId) => {
    navigate(`/products/${productId}`);
  };

  const handleAddProduct = () => {
    navigate('/products/new');
  };

  const featuredProducts = getFeaturedProducts(products);
  const featuredCount = featuredProducts.length;

  const handleToggleFeatured = async (productId) => {
    const product = products.find((item) => item.id === productId);
    if (!product || !isActiveProduct(product)) {
      return;
    }

    const currentFeaturedIds = featuredProducts.map((item) => item.id);
    const isCurrentlyFeatured = currentFeaturedIds.includes(productId);
    let nextFeaturedIds = currentFeaturedIds;

    if (isCurrentlyFeatured) {
      nextFeaturedIds = currentFeaturedIds.filter((id) => id !== productId);
    } else if (currentFeaturedIds.length >= MAX_FEATURED_PRODUCTS) {
      return;
    } else {
      nextFeaturedIds = [...currentFeaturedIds, productId];
    }

    try {
      await ProductService.setFeaturedProducts(nextFeaturedIds);
      await loadProducts();
    } catch (error) {
      console.error('Failed to update featured products:', error);
    }
  };

  return (
    <div className="product-list-container">
      <div className="product-list-header">
        <h1 className="text-2xl font-bold">Liste des Produits</h1>
        <button className="add-product-btn" onClick={handleAddProduct}>
          <AddIcon /> Ajouter un produit
        </button>
      </div>

      <FeaturedProductsPanel featuredProducts={featuredProducts} />

      <div className="product-list-filters">
        <div className="search-container">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-container">
          <FilterIcon className="filter-icon" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="">Toutes les catégories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des produits...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="no-products">
          {products.length === 0 ? (
            <>
              <p className="no-products__title">Bientôt des créations</p>
              <p className="no-products__text">
                Ajoutez votre première pièce pour enrichir votre boutique et votre fiche artisan.
              </p>
              <button
                type="button"
                className="add-product-btn"
                onClick={() => navigate('/products/new')}
              >
                Ajouter un produit
              </button>
            </>
          ) : (
            <p>Aucun produit trouvé.</p>
          )}
        </div>
      ) : (
        <div className="products-grid">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              isFeatured={Boolean(product.is_featured || product.featured_order)}
              featureToggleState={getFeaturedToggleState(
                product,
                featuredCount,
                MAX_FEATURED_PRODUCTS
              )}
              onToggleFeatured={handleToggleFeatured}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductList;
